import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { allTasks, emptyTasks, getTasksDoc, listTaskEntries } from '../../db/tasks';
import { useAsync } from '../hooks/useAsync';
import { useDataVersion } from '../../db/events';
import { BarChart } from '../components/BarChart';
import {
  computeReport,
  RANGE_LABELS,
  shiftAnchor,
  type ReportRange,
} from '../../lib/taskReport';
import {
  TASK_AGGREGATE_LABELS,
  TASK_TYPE_LABELS,
  type TaskEntry,
  type TasksDoc,
} from '../../domain/types';

const fmt = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

/** タスク一覧。ここから各タスクのレポートへ入る。 */
export function ReportsView() {
  const version = useDataVersion();
  const doc = useAsync<TasksDoc>(getTasksDoc, [version], emptyTasks());
  const tasks = allTasks(doc).filter((t) => !t.archived);

  return (
    <div className="view">
      <header className="view-head">
        <div className="view-title">
          <h1>レポート</h1>
          <span className="summary">タスクごとの推移を、週・月・半年・年で見ます</span>
        </div>
        <Link className="today-btn" to="/settings">
          タスクを管理
        </Link>
      </header>
      <div className="view-body">
        {tasks.length === 0 ? (
          <div className="empty">
            <p>タスクがまだありません</p>
            <Link className="primary-btn" to="/settings">
              設定で追加する
            </Link>
          </div>
        ) : (
          <ul className="task-report-list">
            {tasks.map((t) => (
              <li key={t.id}>
                <Link to={`/reports/${t.id}`}>
                  <span className={`task-kind-badge${t.kind === 'weekly' ? ' weekly' : ''}`}>
                    {t.kind === 'daily' ? '毎日' : '毎週'}
                  </span>
                  <span className="cat-name">{t.name}</span>
                  <span className="muted">
                    {TASK_TYPE_LABELS[t.type]}
                    {t.unit ? ` / ${t.unit}` : ''}
                  </span>
                  <span className="muted">›</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** 1タスクのレポート。 */
export function TaskReportView() {
  const { taskId = '' } = useParams();
  const version = useDataVersion();
  const [params, setParams] = useSearchParams();
  const [offset, setOffset] = useState(0);

  const doc = useAsync<TasksDoc>(getTasksDoc, [version], emptyTasks());
  const task = doc.tasks.find((t) => t.id === taskId);

  const range = (params.get('range') as ReportRange) ?? 'month';
  const entries = useAsync<TaskEntry[]>(
    () => (task ? listTaskEntries(task.kind) : Promise.resolve([])),
    [task?.kind, version],
    [],
  );

  if (!task) {
    return (
      <div className="view">
        <div className="view-body empty">
          <p>タスクが見つかりません</p>
          <Link className="primary-btn" to="/reports">
            レポート一覧へ
          </Link>
        </div>
      </div>
    );
  }

  // 週次タスクに「週」レポートは意味がないので出さない
  const ranges: ReportRange[] =
    task.kind === 'weekly' ? ['month', 'half', 'year'] : ['week', 'month', 'half', 'year'];
  const effRange = ranges.includes(range) ? range : 'month';
  const anchor = shiftAnchor(effRange, new Date(), offset);
  const r = computeReport(task, entries, effRange, anchor);

  const rate = r.slots > 0 ? Math.round((r.count / r.slots) * 100) : null;

  return (
    <div className="view">
      <header className="view-head">
        <Link className="nav-btn" to="/reports" aria-label="戻る">
          ‹
        </Link>
        <div className="view-title">
          <h1>{task.name}</h1>
          <span className="summary">
            {TASK_TYPE_LABELS[task.type]}
            {task.type === 'number' &&
              ` / ${TASK_AGGREGATE_LABELS[task.aggregate ?? 'sum']}`}
            {task.unit ? ` / ${task.unit}` : ''}
            {task.target != null && ` / 目標 ${task.target}`}
          </span>
        </div>
      </header>

      <div className="view-body report">
        <div className="search-presets">
          {ranges.map((k) => (
            <button
              key={k}
              className={`chip${effRange === k ? ' is-on' : ''}`}
              onClick={() => {
                const next = new URLSearchParams(params);
                next.set('range', k);
                setParams(next, { replace: true });
                setOffset(0);
              }}
            >
              {RANGE_LABELS[k]}
            </button>
          ))}
        </div>

        <div className="result-bar">
          <button className="nav-btn" onClick={() => setOffset((o) => o - 1)} aria-label="前へ">
            ‹
          </button>
          <span className="report-title">{r.title}</span>
          <button
            className="nav-btn"
            onClick={() => setOffset((o) => Math.min(0, o + 1))}
            disabled={offset >= 0}
            aria-label="次へ"
          >
            ›
          </button>
          <span className="spacer" />
          {offset !== 0 && (
            <button className="chip" onClick={() => setOffset(0)}>
              現在へ
            </button>
          )}
        </div>

        {task.type !== 'text' && (
          <BarChart
            buckets={r.buckets}
            unit={task.unit}
            target={task.target ?? null}
            // 合計やチェック回数は0起点。平均・最新値は「水準」なのでデータ範囲に合わせる
            zeroBased={task.type !== 'number' || (task.aggregate ?? 'sum') === 'sum'}
          />
        )}

        <div className="report-grid">
          {task.type === 'number' && (
            <>
              <Stat label={TASK_AGGREGATE_LABELS[task.aggregate ?? 'sum']}
                    value={task.aggregate === 'avg' ? (r.average != null ? fmt(r.average) : '—')
                                                    : fmt(r.total)}
                    unit={task.unit} />
              <Stat label="平均" value={r.average != null ? fmt(r.average) : '—'} unit={task.unit} />
              <Stat label="最大" value={r.max != null ? fmt(r.max) : '—'} unit={task.unit} />
              <Stat label="最小" value={r.min != null ? fmt(r.min) : '—'} unit={task.unit} />
              {r.hit != null && <Stat label="目標達成" value={`${r.hit}`} unit="回" />}
            </>
          )}
          {task.type === 'check' && (
            <>
              <Stat label="達成" value={`${r.total}`} unit={`/ ${r.slots}`} />
              <Stat label="達成率" value={rate != null ? `${Math.round((r.total / Math.max(r.slots, 1)) * 100)}` : '—'} unit="%" />
              {r.streak != null && <Stat label="現在の連続" value={`${r.streak}`} unit="日" />}
              {r.bestStreak != null && <Stat label="最長連続" value={`${r.bestStreak}`} unit="日" />}
            </>
          )}
          {task.type === 'text' && (
            <>
              <Stat label="記録" value={`${r.count}`} unit={`/ ${r.slots}`} />
              <Stat label="記録率" value={rate != null ? `${rate}` : '—'} unit="%" />
            </>
          )}
        </div>

        {task.type === 'text' && (
          <div className="field">
            <span className="field-label">記録</span>
            {r.buckets.every((b) => b.texts.length === 0) ? (
              <p className="muted">この期間の記録はありません。</p>
            ) : (
              <ul className="text-log">
                {r.buckets.flatMap((b) =>
                  b.texts.map((t) => (
                    <li key={`${b.from}-${t.date}`}>
                      <time>{t.date}</time>
                      <p>{t.text}</p>
                    </li>
                  )),
                )}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className="stat-value">
        {value}
        {unit && <span className="stat-unit">{unit}</span>}
      </span>
    </div>
  );
}
